import type { AnyRouter, ParsedLocation, RouterHistory } from "@tanstack/react-router";

// Two rules, one for the page's scroll regions and one for the window.
//
// **The regions.** Scroll restoration stays the router's everywhere but on the
// routes that opt out of it. A route opts out by declaring
// `leavesScrollToBrowser` in its static data: on every navigation that lands
// on it the router's restoration stands down — it neither restores a saved
// offset (the previous page's, carried onto a push; the entry's own, on Back
// and Forward) nor resets to the top — and the page's scroll regions are left
// to the browser. The spec details page is the one route that opts out
// (#714): the donor's route has no key and its app no scroll restoration, so a
// switch between two of a spec's versions keeps the scroll region mounted and
// nothing writes it, and any other arrival mounts a new region at the top.
//
// **The window** glides to the top on every arrival at a new path — a push,
// Back, Forward, a version switch — and stays where it is on a navigation that
// keeps the path (a search), on every route inside the app chrome: the donor's
// AdminLayout smooth-scrolls it to the top on every change of path, and on
// mount (timmy admin-layout.tsx; #714 D6, owner-ruled 2026-09-30). A route
// declares that it sits there with `glidesWindowToTop`; outside it (the
// donor's public product page, its 404) the window is never written. The
// glide starts where the donor's does, its app restoring no scroll itself:
// from where the reader left the window on a push, and on Back and Forward
// from the entry's own offset, which the browser puts back at once, while the
// page being left is still drawn — so no further down than that page goes.
// The router's own write to the window — its saved offset put back, a reset to
// the top — is undone first: it lands before this rule runs, and the reader's
// offset is read from the scroll events, which reach a listener only a frame
// after the write. A navigation to a hash is left to the router, which
// scrolls the target into view.

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /**
     * Every navigation that lands on this route leaves its scroll regions to
     * the browser (the window's rule, `@/lib/router-scroll`, is every route's).
     */
    leavesScrollToBrowser?: true;
    /**
     * Every navigation that lands on this route or beneath it glides the window
     * to the top on a new path (the app chrome's layout, `@/lib/router-scroll`).
     */
    glidesWindowToTop?: true;
  }
}

// The window's offset as the reader left it, kept by one listener however many
// routers attach (one per test).
let readerOffset = 0;
let trackingReaderOffset = false;

function trackReaderOffset() {
  if (trackingReaderOffset) return;
  trackingReaderOffset = true;
  readerOffset = window.scrollY;
  window.addEventListener("scroll", () => (readerOffset = window.scrollY), { passive: true });
}

// Each history entry's window offset as the reader left it — what the donor's
// browser keeps per entry, across a reload too.
const ENTRY_OFFSETS_KEY = "window-offsets-by-entry";

function readEntryOffsets(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(ENTRY_OFFSETS_KEY) ?? "{}") as Record<string, number>;
  } catch {
    return {};
  }
}

function writeEntryOffsets(offsets: Record<string, number>) {
  try {
    sessionStorage.setItem(ENTRY_OFFSETS_KEY, JSON.stringify(offsets));
  } catch {
    // The offsets then last as long as the page does.
  }
}

const entryKey = (location: ParsedLocation) => location.state.__TSR_key ?? location.href;

const deepestWindowOffset = () =>
  Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

/**
 * The router's `scrollRestoration` option, and the `attach` that gives it the
 * router whose routes it reads. The option is asked at `onRendered`, of the
 * location rendered.
 */
export function createRouteScrollRestoration() {
  let router: AnyRouter | undefined;

  // The entry this router last rendered.
  let renderedEntry: string | undefined;

  function scrollRestoration({ location }: { location: ParsedLocation }) {
    // The same entry rendered again — the router reloading it — is no arrival:
    // nothing is restored, so a glide under way goes on.
    if (renderedEntry !== undefined && entryKey(location) === renderedEntry) return false;
    const [, , foundRoute] = router?.getMatchedRoutes(location.pathname) ?? [];
    return !foundRoute?.options.staticData?.leavesScrollToBrowser;
  }

  function attach(next: AnyRouter) {
    router = next;
    if (typeof window === "undefined") return;
    trackReaderOffset();
    const entryOffsets = readEntryOffsets();
    // Every arrival, so a glide a newer one has overtaken never starts.
    let arrivals = 0;
    // Back or Forward under way: how far down the page being left goes.
    let leavingDepth: number | null = null;
    (next.history as RouterHistory).subscribe(({ action }) => {
      leavingDepth =
        action.type === "PUSH" || action.type === "REPLACE" ? null : deepestWindowOffset();
    });
    next.subscribe("onBeforeLoad", ({ fromLocation }) => {
      if (!fromLocation) return;
      entryOffsets[entryKey(fromLocation)] = readerOffset;
      writeEntryOffsets(entryOffsets);
    });
    // After the router's own handling, which may have written the window.
    next.subscribe("onRendered", ({ fromLocation, toLocation }) => {
      const previous = renderedEntry;
      renderedEntry = entryKey(toLocation);
      // The router's first render (hydration: the window where the document
      // put it), and the same entry rendered again, are no arrivals.
      if (previous === undefined || previous === renderedEntry) return;
      const depth = leavingDepth;
      leavingDepth = null;
      if (toLocation.hash) return;
      const start =
        depth === null ? readerOffset : Math.min(entryOffsets[entryKey(toLocation)] ?? 0, depth);
      window.scrollTo({ top: start, left: 0, behavior: "instant" });
      const arrival = ++arrivals;
      if (fromLocation?.pathname !== toLocation.pathname && glidesWindow(toLocation)) {
        // A frame on, as the donor's effect runs after the commit: a glide
        // begun in the task that wrote the window (the router's restore, the
        // put-back above) can stall where it starts in Chromium.
        requestAnimationFrame(() => {
          if (arrival === arrivals) window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        });
      }
    });
  }

  function glidesWindow(location: ParsedLocation) {
    const [matched] = router?.getMatchedRoutes(location.pathname) ?? [[]];
    return matched.some((route) => route.options.staticData?.glidesWindowToTop);
  }

  return { scrollRestoration, attach };
}
